import { useAdminContext } from '@/contexts/AdminContext';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import {
    AGENT_TYPE,
    EVENT_TYPES,
    MessageType,
    ROLES,
    STATUSES
} from '@common/api/agentService';
import { nextService } from '@common/api/nextService';
import { useCallback, useRef } from 'react';

export const useDashboard = () => {
    const {
        dashboard: {
            createNewAbortController,
            setError,
            addMessage,
            setIsGenerating,
            isGenerating,
            error,
            showGenerating,
            setShowGenerating,
            cancelCurrentRequest,
            currentConversationId,
            setCurrentConversationId,
            messages,
        }
    } = useAdminContext();
    const {user, openSignInModal} = useAuth();
    const { showToast } = useToast();
    const dashboardScrollRef = useRef<HTMLDivElement>(null);

    const handleGenerateDashboard = useCallback(async (
        task: string,
        metadata?: any
    ) => {
        const controller = createNewAbortController();
        const signal = controller.signal;

        if (!user) {
            openSignInModal();
            return;
        }

        if (!task.trim()) {
            setError('Task description is required');
            return;
        }
        
        setShowGenerating(true);
        
        if (dashboardScrollRef.current) {
            dashboardScrollRef.current.scrollTop = 0;
        }

        let targetConversationId = currentConversationId;
        if (!targetConversationId) {
            targetConversationId = 'temp';
            setCurrentConversationId('temp');
        }

        // Add user message using proper MessageType
        const newUserMessage: MessageType = {
            conversation_id: targetConversationId || `dashboard_${Date.now()}`,
            created_at: new Date().toISOString(),
            event_type: EVENT_TYPES.USER_MESSAGE,
            role: ROLES.USER,
            status: STATUSES.COMPLETED,
            content: task,
            json_content: JSON.stringify(metadata),
            agent_type: AGENT_TYPE.AGENT_MODE,
        };

        addMessage(newUserMessage);

        setIsGenerating(true);
        setError(null);

        try {
            await nextService.dashboardStream(
                {
                    task, 
                    metadata,
                    conversation_id: targetConversationId === 'temp' ? undefined : targetConversationId,
                    history: messages
                },
                (data: MessageType) => {
                    if (targetConversationId === 'temp' && data.conversation_id && data.conversation_id !== 'temp') {
                        targetConversationId = data.conversation_id;
                        setCurrentConversationId(data.conversation_id);
                    }
                    // 处理不同类型的消息，参考 useChatSearch 的处理方式
                    addMessage(data);
                },
                (err: any) => {
                    setError(err.message || 'Dashboard generation failed');
                    addMessage({
                        conversation_id: targetConversationId || `dashboard_${Date.now()}`,
                        created_at: new Date().toISOString(),
                        event_type: EVENT_TYPES.ERROR,
                        role: ROLES.ASSISTANT,
                        status: STATUSES.FAILED,
                        content: `Error: ${err.message || 'Dashboard generation failed'}`,
                        json_content: {
                            type: 'error',
                            error: err.message || 'Dashboard generation failed'
                        },
                        agent_type: AGENT_TYPE.AGENT_MODE,
                    });
                },
                signal
            );

        } catch (err: any) {
            console.error('Dashboard generation error:', err);

            // 如果不是取消操作，显示错误提示
            if (err.name !== 'AbortError') {
                setError(err.message || 'Dashboard generation failed');
                showToast(err.message || 'Dashboard generation failed', 'error');

                addMessage({
                    conversation_id: targetConversationId || `dashboard_${Date.now()}`,
                    created_at: new Date().toISOString(),
                    event_type: EVENT_TYPES.ERROR,
                    role: ROLES.ASSISTANT,
                    status: STATUSES.FAILED,
                    content: `Error: ${err.message || 'Dashboard generation failed'}`,
                    json_content: {
                        type: 'error',
                        error: err.message || 'Dashboard generation failed'
                    },
                    agent_type: AGENT_TYPE.AGENT_MODE,
                });
            }
        } finally {
            setIsGenerating(false);
        }
    }, [
        user,
        openSignInModal,
        setError,
        showToast,
        createNewAbortController,
        addMessage,
        setShowGenerating,
        setIsGenerating,
        setCurrentConversationId,
        currentConversationId,
        messages
    ]);


    return {
        // 主要功能
        generateDashboard: handleGenerateDashboard,

        // 引用
        dashboardScrollRef,

        // 状态
        isGenerating,
        showGenerating,
        error,

        // 控制功能
        cancelCurrentRequest
    };
};